import { preferredLanguagesAction } from "../../../Actions/Index";
import { cancelPlaceSelectionAction } from "../../../Actions/LogicActions/Booking/cancelPlaceSelectionAction";
import { deleteShuttleBookOfferAction } from "../../../Actions/LogicActions/AddShuttleTransfer/DeleteShuttleBookOfferAction";
import { cancelQuickExchangeAction } from "../../../Actions/LogicActions/QuickExchange/CancelQuickExchangeAction";
import { getAppDownloadLinkAction } from "../../../Actions/LogicActions/Search/GetAppDownloadLinkAction";
import { selectPreferredLanguagesAction } from "../../../Actions/LogicActions/TranslationLanguage/SelectPreferredLanguagesAction";
import { getBookingDetailStatusAction } from "../../../Actions/UIActions/BookingDetail/GetBookingDetailStatusAction";
import { footerMenuSelectAction } from "../../../Actions/UIActions/Footer/FooterMenuSelectAction";
import { signInAction } from "../../../Actions/UIActions/MyAccount/SignInAction";
import { menuSelectAction } from "../../../Actions/UIActions/Topbar/MenuSelectAction";
import { closeLanguageDropdownAction } from "../../../Actions/UIActions/TranslationLanguage/CloseLanguageDropdownAction";
import { openLanguageDropdownAction } from "../../../Actions/UIActions/TranslationLanguage/OpenLanguageDropdownAction";
import { DEFAULT_LOCALE } from "../../../config/global";
import { ExecutionStatus } from "../../../core/enum/ExecutionStatus";
import { _eventBus } from "../../../core/events/EventBus";
import { StepDefinition } from "../../../core/flows/StepDefinition";
import { StepFlow } from "../../../core/flows/StepFlow";
import { bookingTree, evtTree } from "../../../Orchestrator/ConstantTree";
import { addShuttleTransferTree } from "../../../Orchestrator/ConstantTree";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IGetBookingDetailStatusActionParams, ISignInActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/BookingDetailActionParams";
import { ICancelQuickExchangeAction } from "../../../Orchestrator/Types/Interface/ActionParams/SearchBookingParams";
import { TopBarContext } from "../Context/TopBarContext";

class TopBarInitStep extends StepFlow {
    get stepName() { return "TopBarInit_Step"; }

    async onEventSetup(): Promise<void> {
        this.registerEventSubscription(
            _eventBus.on<{ langCode: string, router: any }, void>({
                eventName: evtTree.TopBar.Init,
                handler: async ({ data }) => {
                    updateRecoilState("localeLanguageAtom", data?.langCode || DEFAULT_LOCALE);
                }
            })
        )

        this.registerEventSubscription(
            _eventBus.on({
                eventName: evtTree.TopBar.SelectLanguage.Click,
                handler: async () => {
                    // Open the dropdown immediately, then make sure the list is
                    // available. The fetch is a no-op when it was already
                    // pre-fetched and cached at startup (see onExecute).
                    await openLanguageDropdownAction.execute()
                    await preferredLanguagesAction.execute()
                    if (preferredLanguagesAction.status.executionStatus === ExecutionStatus.SUCCESS) {
                        const context = this.getCtx<TopBarContext>("topBarContext")
                        if (context) {
                            context.listOfLanguage = preferredLanguagesAction?.result || []
                        }
                    }
                }
            })
        )

        this.registerEventSubscription(
            _eventBus.on<{ langCode: string, router: any }, void>({
                eventName: evtTree.TopBar.SelectLanguage.Select,
                handler: async ({ data }) => {
                    await selectPreferredLanguagesAction.setParams({ language: data?.langCode || "", router: data?.router })
                    await selectPreferredLanguagesAction.execute()
                }
            })
        )

        this.registerEventSubscription(
            _eventBus.on<{ bookingId: string }, void>({
                eventName: evtTree.TopBar.Logo.CancelPlaceSelection,
                handler: async ({ data }) => {
                    const bookingId = data?.bookingId;
                    if (bookingId) {
                        cancelPlaceSelectionAction.setParams({ bookingId });
                        await cancelPlaceSelectionAction.execute();
                        updateRecoilState("seatsChangedOnCheckoutAtom", false);
                    }
                }
            })
        )

        this.registerEventSubscription(
            _eventBus.on<ISignInActionParams, void>({
                eventName: evtTree.TopBar.Login.Click,
                handler: async ({ data }) => {
                    signInAction.setParams({
                        isAuthenticated: data?.isAuthenticated!, login: data?.login!
                    })
                    await signInAction.execute();
                    //Check quick exchange is ingoing or not
                    _eventBus.emit({
                        eventName: bookingTree.QuickExchange.Cancel,
                    });
                }
            })
        )

        this.registerEventSubscription(
            _eventBus.on<{ path: string, isAuthenticated?: boolean, isRoute?: boolean }, void>({
                eventName: evtTree.TopBar.Menu.Select,
                handler: (payload) => {
                    menuSelectAction.setParams(payload.data!)
                    menuSelectAction.execute()
                }
            })
        )

        this.registerEventSubscription(
            _eventBus.on<void, void>({
                eventName: bookingTree.QuickExchange.Cancel,
                handler: async ({ data }) => {
                    const bookingId = sessionStorage.getItem("exchangeBookingId")
                    const exchangeOperationId = sessionStorage.getItem("exchangeOperationId")
                    if (exchangeOperationId && bookingId) {
                        const payload: ICancelQuickExchangeAction = {
                            bookingId: bookingId,
                            exchangeOperationId: exchangeOperationId
                        }
                        cancelQuickExchangeAction.setParams(payload)
                        await cancelQuickExchangeAction.execute()
                    }
                }
            })
        )

        /**
        * I added this event to get the booking detail status
        * This will be used to get the booking detail status when user click on the booking detail
        * This is required here because TopBar Flow is always running
        * So we always have this event running and this event used from the common component
        */
        this.registerEventSubscription(
            _eventBus.on<IGetBookingDetailStatusActionParams, void>({
                eventName: bookingTree.Details.Status.Get,
                handler: async (payload) => {
                    getBookingDetailStatusAction.setParams(payload.data!);
                    await getBookingDetailStatusAction.execute();
                }
            })
        )

        this.registerEventSubscription(
            _eventBus.on<{ path: string, isAuthenticated?: boolean, isRoute?: boolean }, void>({
                eventName: evtTree.Footer.Menu.Select,
                handler: (payload) => {
                    footerMenuSelectAction.setParams(payload.data!)
                    footerMenuSelectAction.execute()
                }
            })
        )

        this.registerEventSubscription(
            _eventBus.on({
                eventName: evtTree.TopBar.getCmsAppDownloadLinkAndQr,
                handler: async () => {
                    getAppDownloadLinkAction.execute();
                }
            })
        )

        this.registerEventSubscription(
            _eventBus.on({
                eventName: evtTree.TopBar.SelectLanguage.Close,
                handler: async () => {
                    await closeLanguageDropdownAction.execute();
                }
            })
        )

        this.registerEventSubscription(
            _eventBus.on({
                eventName: "FLOW_CLOSE",
                handler: async ({ data }: any) => {
                    if (data === "AddShuttleTransferCleanup") {
                        const bookingResponse: any = getRecoilStateValue("bookingResponseAtom");
                        const selectedShuttles: any = getRecoilStateValue("selectedShuttlesAtom");
                        const bookingId = bookingResponse?.booking?.id || bookingResponse?.id || "";

                        if (bookingId && selectedShuttles && Object.keys(selectedShuttles).length > 0) {
                            const currentPath = window.location.pathname;
                            const isOnAddShuttlePage = currentPath.includes('/add-shuttle') || currentPath.includes('/shuttle');

                            if (!isOnAddShuttlePage) {
                                const offersToDelete = Object.entries(selectedShuttles)
                                    .filter(([_, shuttleData]: [string, any]) => shuttleData.offerId)
                                    .map(([_, shuttleData]: [string, any]) => shuttleData.offerId);

                                for (const offerId of offersToDelete) {
                                    try {
                                        deleteShuttleBookOfferAction.setParams({ bookingId: bookingId, offerId: offerId || '' });
                                        await deleteShuttleBookOfferAction.execute();

                                        await new Promise((resolve: any) => setTimeout(resolve, 300));
                                    } catch (error) {
                                        console.error("Error deleting shuttle offer:", error);
                                    }
                                }

                                updateRecoilState("selectedShuttlesAtom", {});
                            }
                        }
                    }
                }
            })
        )
    }

    async onExecute(): Promise<void> {
        // Pre-fetch the preferred-languages list as soon as the customer web
        // portal opens (TopBar flow starts at app startup), and cache it for
        // the whole session. apiRequest waits for TOKEN_READY internally, so
        // this is safe to fire before auth has finished bootstrapping. This
        // does not open the language dropdown.
        void preferredLanguagesAction.execute();
    }
}

export const TopBarInitStepDeclaration = new StepDefinition({
    stepName: "TopBarInit_Step",
    stepClass: TopBarInitStep
});